import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/ui/alert'
import { Badge } from '@repo/ui/components/ui/badge'
import { Button } from '@repo/ui/components/ui/button'
import { Input } from '@repo/ui/components/ui/input'
import { Label } from '@repo/ui/components/ui/label'
import { Separator } from '@repo/ui/components/ui/separator'
import { createFileRoute } from '@tanstack/react-router'
import {
  AlertCircle,
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  Clock,
  type LucideIcon,
  Mail,
  MapPin,
  Phone,
  XCircle,
} from 'lucide-react'
import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'

type ManageBookingSearch = {
  id?: string
  email?: string
}

export const Route = createFileRoute('/booking/manage')({
  validateSearch: (search: Record<string, unknown>): ManageBookingSearch => ({
    id: typeof search.id === 'string' ? search.id : undefined,
    email: typeof search.email === 'string' ? search.email : undefined,
  }),
  component: ManageBookingPage,
})

function ManageBookingPage() {
  const search = Route.useSearch()
  const [appointmentId, setAppointmentId] = useState(search.id ?? '')
  const [email, setEmail] = useState(search.email ?? '')
  const [submitted, setSubmitted] = useState(() => Boolean(search.id && search.email))
  const [cancelled, setCancelled] = useState(false)

  const lookupInput = useMemo(
    () => ({
      id: appointmentId.trim(),
      email: email.trim(),
    }),
    [appointmentId, email]
  )

  const appointmentQuery = trpc.booking.getAppointment.useQuery(lookupInput, {
    enabled: submitted && lookupInput.id.length > 0 && lookupInput.email.length > 0,
    retry: false,
  })

  const cancelAppointment = trpc.booking.cancelAppointment.useMutation({
    onSuccess: async () => {
      setCancelled(true)
      toast.success('Booking cancelled')
      await appointmentQuery.refetch()
    },
    onError: (error) => {
      toast.error(error.message || 'Unable to cancel booking')
    },
  })

  useEffect(() => {
    if (search.id) setAppointmentId(search.id)
    if (search.email) setEmail(search.email)
    if (search.id && search.email) setSubmitted(true)
  }, [search.id, search.email])

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setCancelled(false)
    setSubmitted(true)
  }

  const data = appointmentQuery.data
  const appointment = data?.appointment
  const isCancelled = cancelled || appointment?.status === 'cancelled'
  const canCancel = appointment?.status === 'confirmed' && !cancelAppointment.isPending

  return (
    <main className="min-h-[100dvh] bg-background text-foreground">
      <div
        className="app-bg-grid pointer-events-none fixed inset-0 opacity-35"
        aria-hidden="true"
      />
      <div className="relative mx-auto flex min-h-[100dvh] w-full max-w-5xl flex-col px-4 py-6 sm:px-6 lg:px-8">
        <a
          href="/"
          className="mb-8 inline-flex w-fit items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Zippity
        </a>

        <section className="grid flex-1 items-start gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-10">
          <div className="pt-2 lg:pt-8">
            <p className="app-kicker">Customer booking</p>
            <h1 className="mt-4 max-w-xl text-balance text-4xl font-semibold tracking-tight md:text-5xl">
              View or cancel your appointment.
            </h1>
            <p className="mt-5 max-w-lg text-base leading-7 text-muted-foreground">
              Use the email from your confirmation and the booking reference to confirm the time,
              address, and provider details.
            </p>

            <div className="mt-8 grid gap-3 text-sm text-muted-foreground sm:grid-cols-2 lg:grid-cols-1">
              <div className="flex items-center gap-3">
                <span className="flex size-9 items-center justify-center rounded-[6px] border bg-background">
                  <CheckCircle2 className="size-4 text-primary" aria-hidden="true" />
                </span>
                Secure lookup by booking reference and email.
              </div>
              <div className="flex items-center gap-3">
                <span className="flex size-9 items-center justify-center rounded-[6px] border bg-background">
                  <XCircle className="size-4 text-primary" aria-hidden="true" />
                </span>
                Cancel confirmed appointments before contacting the operator.
              </div>
            </div>
          </div>

          <div className="app-premium-panel rounded-[8px] p-4 shadow-[var(--app-shadow-soft)] sm:p-6">
            <form onSubmit={handleSubmit} className="grid gap-4" noValidate>
              <div className="grid gap-2">
                <Label htmlFor="appointment-id">Booking reference</Label>
                <Input
                  id="appointment-id"
                  value={appointmentId}
                  onChange={(event) => setAppointmentId(event.target.value)}
                  autoComplete="off"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="booking-email">Email address</Label>
                <Input
                  id="booking-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  required
                />
              </div>
              <Button
                type="submit"
                className="mt-1 transition-transform active:scale-[0.98]"
                disabled={appointmentQuery.isFetching}
              >
                {appointmentQuery.isFetching ? 'Looking up...' : 'View booking'}
              </Button>
            </form>

            <Separator className="my-6" />

            {!submitted ? (
              <EmptyState />
            ) : appointmentQuery.isLoading || appointmentQuery.isFetching ? (
              <LoadingState />
            ) : appointmentQuery.isError ? (
              <Alert variant="destructive" className="rounded-[6px]">
                <AlertCircle className="size-4" aria-hidden="true" />
                <AlertTitle>Booking not found</AlertTitle>
                <AlertDescription>
                  Check the booking reference and email address from your confirmation message.
                </AlertDescription>
              </Alert>
            ) : appointment ? (
              <div className="grid gap-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {data?.operator?.businessName ?? 'Appointment'}
                    </p>
                    <h2 className="mt-1 text-2xl font-semibold tracking-tight">
                      {data?.service?.name ?? 'Service appointment'}
                    </h2>
                  </div>
                  <Badge variant={isCancelled ? 'destructive' : 'default'}>
                    {isCancelled ? 'Cancelled' : appointment.status}
                  </Badge>
                </div>

                <div className="grid gap-3 rounded-[6px] border bg-background/70 p-4 text-sm">
                  <DetailRow icon={CalendarDays} label="Date" value={appointment.date} />
                  <DetailRow
                    icon={Clock}
                    label="Time"
                    value={`${appointment.startTime} - ${appointment.endTime}`}
                  />
                  {appointment.addressText ? (
                    <DetailRow icon={MapPin} label="Address" value={appointment.addressText} />
                  ) : null}
                  {data?.operator?.phone ? (
                    <DetailRow icon={Phone} label="Operator phone" value={data.operator.phone} />
                  ) : null}
                </div>

                {isCancelled ? (
                  <Alert className="rounded-[6px]">
                    <CheckCircle2 className="size-4" aria-hidden="true" />
                    <AlertTitle>This booking is cancelled</AlertTitle>
                    <AlertDescription>
                      The operator has been updated. Contact them directly if you need a new time.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="flex flex-col gap-3 rounded-[6px] border border-destructive/20 bg-destructive/5 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-medium text-destructive">Need to cancel?</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        This cancels the appointment immediately.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="destructive"
                      disabled={!canCancel}
                      onClick={() => cancelAppointment.mutate(lookupInput)}
                      className="transition-transform active:scale-[0.98]"
                    >
                      {cancelAppointment.isPending ? 'Cancelling...' : 'Cancel booking'}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <EmptyState />
            )}
          </div>
        </section>
      </div>
    </main>
  )
}

function EmptyState() {
  return (
    <div className="grid place-items-center rounded-[6px] border border-dashed bg-background/60 px-4 py-10 text-center">
      <Mail className="size-8 text-muted-foreground" aria-hidden="true" />
      <p className="mt-3 font-medium">Enter your booking details</p>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">
        Your confirmation email includes the booking reference. The email address must match the one
        used when booking.
      </p>
    </div>
  )
}

function LoadingState() {
  return (
    <output className="grid gap-3" aria-label="Loading booking">
      <div className="h-6 w-2/3 animate-pulse rounded-[4px] bg-muted" />
      <div className="h-20 animate-pulse rounded-[6px] bg-muted" />
      <div className="h-16 animate-pulse rounded-[6px] bg-muted" />
    </output>
  )
}

function DetailRow({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon
  label: string
  value: string
}) {
  return (
    <div className="grid grid-cols-[1.25rem_5.5rem_1fr] items-start gap-3">
      <Icon className="mt-0.5 size-4 text-muted-foreground" aria-hidden="true" />
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  )
}
